import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useToast, useDisclosure } from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import { useMenuTitle } from '../hooks/useMenuTitle';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Grid,
  Heading,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  Spacer,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  Center,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  CloseButton,
  HStack,
  UnorderedList,
  ListItem
} from '@chakra-ui/react';
import {
  SearchIcon,
  QuestionIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  CloseIcon
} from '@chakra-ui/icons';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { format } from 'date-fns';
import { supabase } from '../supabaseClient';

const MessageHistoryModal = ({ isOpen, onClose, recipientId, recipientName }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay />
      <ModalContent maxW="90vw" maxH="90vh" h="90vh">
        <ModalHeader pb={2}>Message History - {recipientName}</ModalHeader>
        <ModalCloseButton />
        <ModalBody p={0}>
          <MessageHistory recipientId={recipientId} isModal={true} />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const MessageHistory = ({ recipientId: propRecipientId, isModal = false, refreshKey = 0 }) => {
  const { recipientId: urlRecipientId } = useParams();
  const recipientId = propRecipientId || urlRecipientId;
  const menuTitle = useMenuTitle();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('sent_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [timeFilter, setTimeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const PAGE_SIZE = 50;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  useEffect(() => {
    setPage(0);
    fetchMessages();
  }, [recipientId, timeFilter, statusFilter, refreshKey]);

  useEffect(() => {
    if (page > 0) {
      fetchMessages();
    }
  }, [page]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('message_history_with_sender')
        .select('*');

      // Apply time filter
      if (timeFilter !== 'all') {
        const now = new Date();
        let startDate = new Date();
        
        switch (timeFilter) {
          case 'today':
            startDate.setHours(0, 0, 0, 0);
            break;
          case 'week':
            startDate.setDate(now.getDate() - 7);
            break;
          case 'month':
            startDate.setDate(now.getDate() - 30);
            break;
        }
        
        query = query.gte('sent_at', startDate.toISOString());
      }

      // Apply status filter
      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      // If recipientId is provided, filter messages for that recipient
      if (recipientId) {
        query = query.eq('recipient_id', recipientId);
      }

      // Add pagination
      query = query
        .order('sent_at', { ascending: false })
        .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      // Check if we have more pages
      setHasMore(data?.length === PAGE_SIZE);
      
      if (page === 0) {
        setMessages(data || []);
      } else {
        setMessages(prev => [...prev, ...(data || [])]);
      }
      setError(null);
    } catch (error) {
      console.error('Error fetching messages:', error);
      setError('Failed to load messages');
      toast({
        title: 'Error loading messages',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const loadMore = () => {
    if (!loading && hasMore) {
      setPage(prev => prev + 1);
    }
  };

  const observerTarget = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => observer.disconnect();
  }, [hasMore, loading]);

  // Filter and sort messages
  const filteredAndSortedMessages = useMemo(() => {
    let result = [...messages];
    
    // Filter by search term
    if (searchQuery) {
      const lowercaseSearch = searchQuery.toLowerCase();
      result = result.filter(msg => 
        msg.recipient_name?.toLowerCase().includes(lowercaseSearch) ||
        msg.recipient_phone?.toLowerCase().includes(lowercaseSearch) ||
        msg.message_text?.toLowerCase().includes(lowercaseSearch)
      );
    }
    
    // Sort messages
    result.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      
      if (aValue === null) return 1;
      if (bValue === null) return -1;
      
      if (sortField === 'sent_at') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      const comparison = aValue.toString().localeCompare(bValue.toString());
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    
    return result;
  }, [messages, searchQuery, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <Icon as={FaSort} />;
    return sortDirection === 'asc' ? <Icon as={FaSortUp} /> : <Icon as={FaSortDown} />;
  };

  return (
    <Box p={isModal ? 2 : 4} h={isModal ? "calc(90vh - 80px)" : "calc(100vh - 100px)"} display="flex" flexDirection="column">
      {!isModal && (
        <HStack justify="space-between" mb={4}>
          <Heading size="lg">{menuTitle}</Heading>
          <IconButton
            icon={<QuestionIcon />}
            variant="ghost"
            aria-label="Help"
            size="sm"
            onClick={onOpen}
          />
        </HStack>
      )}

      <Flex gap={2} mb={2}>
        <InputGroup size="sm">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.300" />
          </InputLeftElement>
          <Input
            placeholder="Search by name, phone number, or message"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="sm"
          />
          {searchQuery && (
            <InputRightElement>
              <IconButton
                icon={<CloseIcon />}
                size="xs"
                variant="ghost"
                colorScheme="gray"
                aria-label="Clear search"
                onClick={() => setSearchQuery('')}
              />
            </InputRightElement>
          )}
        </InputGroup>

        <Select
          w="200px"
          size="sm"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All Statuses</option>
          <option value="sent">Sent</option>
          <option value="failed">Failed</option>
          <option value="pending">Pending</option>
        </Select>

        <Select
          w="200px"
          size="sm"
          value={timeFilter}
          onChange={(e) => setTimeFilter(e.target.value)}
        >
          <option value="all">All Time</option>
          <option value="today">Today</option>
          <option value="week">Last 7 Days</option>
          <option value="month">Last 30 Days</option>
        </Select>
      </Flex>

      {error && (
        <Alert status="error" mb={2}>
          <AlertIcon />
          {error}
        </Alert>
      )}

      {loading && page === 0 ? (
        <Center flex={1}>
          <Spinner size="xl" />
        </Center>
      ) : (
        <Box overflowY="auto" flex={1} mx={-4} px={4}>
          <Table variant="simple" size="xs">
            <Thead position="sticky" top={0} bg="white" zIndex={1}>
              <Tr>
                <Th py={0} px={1} fontSize="2xs" fontWeight="normal" cursor="pointer" onClick={() => handleSort('sent_at')}>
                  <HStack spacing={1} display="inline-flex" alignItems="center">
                    <Text>DATE</Text>
                    {getSortIcon('sent_at')}
                  </HStack>
                </Th>
                <Th py={0} px={1} fontSize="2xs" fontWeight="normal" cursor="pointer" onClick={() => handleSort('recipient_name')}>
                  <HStack spacing={1} display="inline-flex" alignItems="center">
                    <Text>RECIPIENT</Text>
                    {getSortIcon('recipient_name')}
                  </HStack>
                </Th>
                <Th py={0} px={1} fontSize="2xs" fontWeight="normal" cursor="pointer" onClick={() => handleSort('recipient_phone')}>
                  <HStack spacing={1} display="inline-flex" alignItems="center">
                    <Text>PHONE</Text>
                    {getSortIcon('recipient_phone')}
                  </HStack>
                </Th>
                <Th py={0} px={1} fontSize="2xs" fontWeight="normal" cursor="pointer" onClick={() => handleSort('status')}>
                  <HStack spacing={1} display="inline-flex" alignItems="center">
                    <Text>STATUS</Text>
                    {getSortIcon('status')}
                  </HStack>
                </Th>
                <Th py={0} px={1} fontSize="2xs" fontWeight="normal" cursor="pointer" onClick={() => handleSort('message_text')}>
                  <HStack spacing={1} display="inline-flex" alignItems="center">
                    <Text>MESSAGE</Text>
                    {getSortIcon('message_text')}
                  </HStack>
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredAndSortedMessages.map((message) => (
                <Tr key={message.id}>
                  <Td py={0} px={1} fontSize="xs" whiteSpace="nowrap">
                    {format(new Date(message.sent_at), 'MMM d, yyyy h:mm a')}
                  </Td>
                  <Td py={0} px={1} fontSize="xs">{message.recipient_name}</Td>
                  <Td py={0} px={1} fontSize="xs">{message.recipient_phone}</Td>
                  <Td py={0} px={1} fontSize="xs">
                    <Text color={message.status === 'sent' ? 'green.500' : message.status === 'failed' ? 'red.500' : 'yellow.500'}>
                      {message.status}
                    </Text>
                  </Td>
                  <Td py={0} px={1} fontSize="xs" maxW="300px" isTruncated>{message.message_text}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
          {loading && (
            <Center py={4}>
              <Spinner size="sm" />
            </Center>
          )}
          <Box ref={observerTarget} h="20px" />
        </Box>
      )}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Message History Help</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack align="stretch" spacing={3}>
              <Text>
                The Message History page shows a complete record of all messages sent through the system:
              </Text>
              <Box>
                <Text fontWeight="bold" mb={1}>Features:</Text>
                <UnorderedList spacing={1} pl={4}>
                  <ListItem>View all sent messages</ListItem>
                  <ListItem>Filter by status (Sent, Failed, Pending)</ListItem>
                  <ListItem>Filter by time period</ListItem>
                  <ListItem>Search across all fields</ListItem>
                  <ListItem>Sort by any column</ListItem>
                </UnorderedList>
              </Box>
              <Box>
                <Text fontWeight="bold" mb={1}>Status Colors:</Text>
                <UnorderedList spacing={1} pl={4}>
                  <ListItem>
                    <Text color="green.500" as="span">Sent</Text>
                    {' - '}Message was successfully delivered
                  </ListItem>
                  <ListItem>
                    <Text color="red.500" as="span">Failed</Text>
                    {' - '}Message delivery failed
                  </ListItem>
                  <ListItem>
                    <Text color="yellow.500" as="span">Pending</Text>
                    {' - '}Message is queued for delivery
                  </ListItem>
                </UnorderedList>
              </Box>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export { MessageHistoryModal };
export default MessageHistory;
